import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../config/api'
import './Services.css'

function Services() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  
  // Track which service is currently playing video
  const [playingVideoId, setPlayingVideoId] = useState(null)
  const videoRefs = useRef({})

  const getDescriptionText = (desc) => {
    if (typeof desc === 'string') return desc
    if (Array.isArray(desc)) {
      return desc
        .map(block => block.children?.map(child => child.text).join('') || '')
        .join('\n')
    }
    return ''
  }

  const formatMediaUrl = (url) => {
    if (!url) return null
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL
    const path = url.startsWith('/') ? url : `/${url}`
    return `${baseUrl}${path}`
  }

  const getMediaUrl = (media) => {
    if (!media) return null
    const data = media.data || media
    const item = Array.isArray(data) ? data[0] : data
    if (!item) return null
    const relativeUrl = item.attributes?.url || item.url || null
    return formatMediaUrl(relativeUrl)
  }

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_URL}/api/services?populate=*`)
        const data = await response.json()
        const servicesData = (data.data || [])
          .map(item => ({
            id: item.id,
            ...(item.attributes || item)
          }))
          .sort((a, b) => a.id - b.id)

        setServices(servicesData)
      } catch (error) {
        console.error('Error fetching services:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchServices()
  }, [])

  // Handle playing/pausing video with sound on service click
  const handleServiceClick = (serviceId, e) => {
    e.stopPropagation()

    // Toggle description expand state
    setExpandedId(expandedId === serviceId ? null : serviceId)

    const videoEl = videoRefs.current[serviceId]

    if (playingVideoId === serviceId) {
      // Pause if clicking the currently active service
      if (videoEl) {
        videoEl.pause()
      }
      setPlayingVideoId(null)
    } else {
      // Pause all other videos
      Object.keys(videoRefs.current).forEach((id) => {
        if (videoRefs.current[id]) {
          videoRefs.current[id].pause()
          videoRefs.current[id].currentTime = 0
        }
      })

      // Start playing selected video with audio
      if (videoEl) {
        videoEl.muted = false
        videoEl.play().catch(err => console.warn('Video play error:', err))
        setPlayingVideoId(serviceId)
      }
    }
  }

  return (
    <section className="features">
      <h2>Onze Diensten</h2>

      {loading ? (
        <div className="loading">Diensten worden geladen...</div>
      ) : services.length === 0 ? (
        <div className="no-services">
          <p>Nog geen diensten beschikbaar.</p>
        </div>
      ) : (
        <div className="features-list">
          {services.map((service, index) => {
            const videoUrl = getMediaUrl(service.video)
            const imageUrl = getMediaUrl(service.image)
            const isPlaying = playingVideoId === service.id
            const isExpanded = expandedId === service.id

            return (
              <div
                key={service.id}
                className={`feature-row ${isExpanded ? 'expanded' : ''}`}
                onClick={(e) => handleServiceClick(service.id, e)}
                style={{ cursor: 'pointer' }}
              >
                {/* Media wrapper on top */}
                <div className="service-media-wrapper" style={{ position: 'relative' }}>
                  {videoUrl ? (
                    <>
                      <video
                        ref={(el) => (videoRefs.current[service.id] = el)}
                        src={videoUrl}
                        className="service-video"
                        poster={imageUrl || undefined}
                        loop
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
                      />
                      
                      {/* Play overlay badge */}
                      <div 
                        className="play-badge"
                        style={{
                          position: 'absolute',
                          bottom: '12px',
                          right: '12px',
                          background: isPlaying ? 'rgba(0, 0, 0, 0.75)' : 'rgba(49, 130, 206, 0.9)',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '600',
                          pointerEvents: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {isPlaying ? '⏸️ Pauzeren' : '▶️ Bekijk Video'}
                      </div>
                    </>
                  ) : imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={service.title}
                      className="service-image"
                      loading={index < 2 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  ) : null}
                </div>

                {/* Content area below */}
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p className={`service-description ${isExpanded ? 'expanded' : ''}`}>
                    {getDescriptionText(service.description)}
                  </p>
                  <Link
                    to={`/contact?service=${encodeURIComponent(service.title)}`}
                    className="service-cta-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Vraag prijs aan
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default Services